/**
 * Server-backed probe: Calendly booking URLs return 404 when the user/slug does not exist.
 * Used to hide #calendly-* CTAs when SFCC has a syntactically valid but dead Calendly segment.
 *
 * @see server/middleware/calendly-booking-reachability.js
 */

import {useEffect, useMemo, useRef, useState} from 'react';
import {useAccessToken} from '@salesforce/commerce-sdk-react';

const CACHE_PREFIX = 'kl-cal-reach:';
const CACHE_TTL_MS = 10 * 60 * 1000;

/** When probing is off, downstream treats both slots as available (no gating). */
const SKIP_PROBE_STATE = Object.freeze({
  pending: false,
  fetchError: false,
  inPersonReachable: true,
  videoReachable: true,
});

/** @type {Map<string, Promise<{inPersonReachable: boolean, videoReachable: boolean}>>} */
const inflightByKey = new Map();

/**
 * @param {string} inPersonUrl
 * @param {string} videoUrl
 * @returns {string}
 */
const makeCacheKey = (inPersonUrl, videoUrl) => `${inPersonUrl || ''}\n${videoUrl || ''}`;

/**
 * @param {string} key
 * @returns {{ inPersonReachable: boolean, videoReachable: boolean } | null}
 */
const readSessionCache = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(CACHE_PREFIX + key);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(CACHE_PREFIX + key);

      return null;
    }

    return {
      inPersonReachable: Boolean(parsed.inPersonReachable),
      videoReachable: Boolean(parsed.videoReachable),
    };
  } catch {
    return null;
  }
};

/**
 * @param {string} key
 * @param {{ inPersonReachable: boolean, videoReachable: boolean }} value
 */
const writeSessionCache = (key, value) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({...value, ts: Date.now()})
    );
  } catch {
    // Ignore quota / private mode
  }
};

/**
 * @param {Object} options
 * @param {boolean} options.enabled - Run probe when true and at least one URL is non-empty
 * @param {string | null} options.inPersonUrl - Trusted booking URL or null
 * @param {string | null} options.videoUrl - Trusted booking URL or null
 * @returns {{ pending: boolean, fetchError: boolean, inPersonReachable: boolean, videoReachable: boolean }}
 */
export function useCalendlyBookingReachability({enabled, inPersonUrl, videoUrl}) {
  const {getTokenWhenReady} = useAccessToken();
  const getTokenWhenReadyRef = useRef(getTokenWhenReady);

  getTokenWhenReadyRef.current = getTokenWhenReady;

  const key = useMemo(() => makeCacheKey(inPersonUrl || '', videoUrl || ''), [inPersonUrl, videoUrl]);

  const [state, setState] = useState(() => ({
    pending: Boolean(enabled && (inPersonUrl || videoUrl)),
    fetchError: false,
    inPersonReachable: false,
    videoReachable: false,
  }));

  useEffect(() => {
    if (!enabled) {
      setState((prev) => {
        if (
          prev.pending === false &&
          prev.fetchError === false &&
          prev.inPersonReachable === true &&
          prev.videoReachable === true
        ) {
          return prev;
        }

        return {...SKIP_PROBE_STATE};
      });

      return;
    }

    if (!inPersonUrl && !videoUrl) {
      setState((prev) => {
        if (
          prev.pending === false &&
          prev.fetchError === false &&
          prev.inPersonReachable === false &&
          prev.videoReachable === false
        ) {
          return prev;
        }

        return {
          pending: false,
          fetchError: false,
          inPersonReachable: false,
          videoReachable: false,
        };
      });

      return;
    }

    const cached = readSessionCache(key);

    if (cached) {
      setState((prev) => {
        if (
          prev.pending === false &&
          prev.fetchError === false &&
          prev.inPersonReachable === cached.inPersonReachable &&
          prev.videoReachable === cached.videoReachable
        ) {
          return prev;
        }

        return {
          pending: false,
          fetchError: false,
          inPersonReachable: cached.inPersonReachable,
          videoReachable: cached.videoReachable,
        };
      });

      return;
    }

    setState((prev) => {
      if (
        prev.pending === true &&
        prev.fetchError === false &&
        prev.inPersonReachable === false &&
        prev.videoReachable === false
      ) {
        return prev;
      }

      return {
        pending: true,
        fetchError: false,
        inPersonReachable: false,
        videoReachable: false,
      };
    });

    let cancelled = false;

    const run = async () => {
      let promise = inflightByKey.get(key);

      if (!promise) {
        promise = (async () => {
          const token = await getTokenWhenReadyRef.current();

          const response = await fetch('/server/calendly-booking-reachability', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({inPersonUrl, videoUrl}),
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();

          return {
            inPersonReachable: Boolean(data.inPersonReachable),
            videoReachable: Boolean(data.videoReachable),
          };
        })().finally(() => {
          inflightByKey.delete(key);
        });

        inflightByKey.set(key, promise);
      }

      try {
        const result = await promise;

        if (cancelled) {
          return;
        }

        writeSessionCache(key, result);
        setState({
          pending: false,
          fetchError: false,
          inPersonReachable: result.inPersonReachable,
          videoReachable: result.videoReachable,
        });
      } catch {
        if (cancelled) {
          return;
        }

        // Network / 5xx: keep CTAs available (trust URL shape) so a probe outage does not blank booking sitewide
        setState({
          pending: false,
          fetchError: true,
          inPersonReachable: true,
          videoReachable: true,
        });
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [enabled, key, inPersonUrl, videoUrl]);

  return state;
}
